// aidasapps/frontend/src/hooks/useChartData.js
import { useState, useEffect, useRef } from 'react';
import { interactiveFetch } from '../api';

export const useChartData = (widget, dataset, additionalFilters = {}, customMeasures = [], dataModel = null, filtersReady = true) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Data Dependency Key
    const config = widget?.config;
    const widgetType = config?.chartType || widget?.type;  // Support both config.chartType and widget.type

    // Check if any data fields are configured (Expanded for all chart types)
    const hasDataConfig = (dataset?.id || dataModel?.id) && (
        (config?.xAxis?.length > 0) ||
        (config?.yAxis?.length > 0) ||
        (config?.series?.length > 0) ||
        // Pivot Table
        (config?.rows?.length > 0) ||
        (config?.columns?.length > 0) ||
        (config?.values?.length > 0) ||
        // Sankey
        (config?.source?.length > 0) ||
        (config?.target?.length > 0) ||
        // Combo
        (config?.lineMeasures?.length > 0) ||
        // Other (Gauge, KPI, etc might use target/min/max but usually have yAxis too)
        (config?.element?.length > 0) || // Generic fallback
        // Legacy / Table specific
        (widgetType === 'table' && config?.query_config?.selected_columns?.length > 0)
    );

    // Data Dependency Key
    const dataKey = JSON.stringify({
        datasetId: dataset?.id,
        dataModelId: dataModel?.id,  // trigger refetch on model change
        widgetType: widgetType,  // Track widget type changes from config
        widgetTypeActual: widget?.type,  // Track actual widget.type (authoritative for combo etc.)
        xAxis: config?.xAxis,
        yAxis: config?.yAxis,
        series: config?.series,
        legend: config?.legend,  // Track heatmap legend (y-axis values)
        columns: config?.columns,  // Track table columns
        // New Fields for Pivot / Sankey / Combo
        rows: config?.rows,
        values: config?.values,
        source: config?.source,
        target: config?.target,
        lineMeasures: config?.lineMeasures,

        filters: config?.filters,
        additionalFilters: additionalFilters, // Trigger refetch on global filter change
        extraFields: config?.style?.tooltip?.extraFields,
        query_config: config?.query_config,
        data_limit: config?.data?.limit,
        data_sort: config?.data?.sort_by,
        sort_dir_legacy: config?.query_config?.sortDir || config?.query_config?.sortOrder,
        custom_measures: customMeasures, // Trigger refetch on formula update
        filtersReady, // Block fetch until filters are initialized
    });

    const prevKeyRef = useRef(null);

    useEffect(() => {
        const prevKey = prevKeyRef.current;
        prevKeyRef.current = dataKey; // Update for next time

        // Clear stale error immediately so "Data Unavailable" doesn't persist
        // while re-querying after a field is added/changed.
        setError(null);

        // CRITICAL: Don't query if neither dataset nor data model is loaded yet
        if (!dataset?.id && !dataModel?.id) {
            setData([]);
            return;
        }

        if (!hasDataConfig) {
            setData([]);
            return;
        }

        // FILTER GATE: Don't fetch until auto-selected filters are resolved
        if (!filtersReady) {
            return;
        }

        // Abort Controller for Request Cancellation
        const controller = new AbortController();

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // 1. Construct WidgetQueryRequest Payload

                const normalizeAgg = (agg) => {
                    if (!agg) return 'sum';
                    const lower = agg.toLowerCase();
                    if (lower === 'average') return 'avg';
                    if (lower === 'unique') return 'count_distinct';
                    if (lower === 'sum' || lower === 'avg' || lower === 'min' || lower === 'max' || lower === 'count' || lower === 'distinct' || lower === 'count_distinct') {
                        return lower;
                    }
                    return 'sum';
                };

                // Identify Chart Type: widget.type is always authoritative.
                // config.chartType can be stale (e.g. 'bar') after user switches chart type to 'combo',
                // because GalleryView only updates widget.type, not config.chartType.
                // Using stale config.chartType would cause the combo branch to be skipped,
                // leaving lineMeasures out of y_series entirely.
                const chartType = widget.type || config.chartType;

                let xField = null;
                let groupField = config.query_config?.groupField || null;
                let sourceSeries = config.yAxis || config.series || [];

                // --- TYPE SPECIFIC MAPPING ---
                if (chartType === 'heatmap') {
                    // Heatmap: X=Dimension, Y=GroupDimension, Value=Measure
                    if (Array.isArray(config.xAxis) && config.xAxis.length > 0) {
                        xField = typeof config.xAxis[0] === 'string' ? config.xAxis[0] : config.xAxis[0].name;
                    }
                    if (Array.isArray(config.yAxis) && config.yAxis.length > 0) {
                        // Map Y-Axis config to Group Field
                        groupField = typeof config.yAxis[0] === 'string' ? config.yAxis[0] : config.yAxis[0].name;
                    }
                    // Value comes from Legend zone in our Toolbox mapping
                    if (config.legend && config.legend.length > 0) {
                        sourceSeries = config.legend;
                    } else {
                        sourceSeries = []; // No value mapped
                    }

                } else if (['scatter', 'bubble'].includes(config.chartType)) {
                    // Scatter: X=Measure, Y=Measure, Legend=Dimension (Dot)
                    if (Array.isArray(config.legend) && config.legend.length > 0) {
                        xField = typeof config.legend[0] === 'string' ? config.legend[0] : config.legend[0].name;
                    }
                    // Collect measures from X and Y axis zones
                    sourceSeries = [...(config.xAxis || []), ...(config.yAxis || [])];

                } else if (['kpi', 'gauge'].includes(config.chartType)) {
                    // KPI/Gauge: Map yAxis, target, comparison with roles
                    const main = (config.yAxis || []).map(s => ({ ...s, role: 'yAxis' }));
                    const targets = (config.target || []).map(s => ({ ...s, role: 'target' }));
                    const comparisons = (config.comparison || []).map(s => ({ ...s, role: 'comparison' }));

                    sourceSeries = [...main, ...targets, ...comparisons];

                } else if (chartType === 'sankey') {
                    // Sankey: source -> xField, target -> groupField, value -> sourceSeries
                    if (Array.isArray(config.source) && config.source.length > 0) {
                        xField = typeof config.source[0] === 'string' ? config.source[0] : (config.source[0].name || config.source[0].field);
                    }
                    if (Array.isArray(config.target) && config.target.length > 0) {
                        groupField = typeof config.target[0] === 'string' ? config.target[0] : (config.target[0].name || config.target[0].field);
                    }
                    sourceSeries = [
                        ...(config.source || []).map(s => ({ ...s, role: 'source' })),
                        ...(config.target || []).map(s => ({ ...s, role: 'target' })),
                        ...(config.value || []).map(s => ({ ...s, role: 'value' }))
                    ];

                } else if (chartType === 'pivot') {
                    // Pivot: rows -> xField, columns -> groupField, values -> series
                    if (Array.isArray(config.rows) && config.rows.length > 0) {
                        xField = typeof config.rows[0] === 'string' ? config.rows[0] : (config.rows[0].name || config.rows[0].field);
                    }
                    if (Array.isArray(config.columns) && config.columns.length > 0) {
                        groupField = typeof config.columns[0] === 'string' ? config.columns[0] : (config.columns[0].name || config.columns[0].field);
                    }
                    sourceSeries = config.values || [];

                } else if (chartType === 'combo') {
                    // Combo: xAxis -> xField, yAxis (Bars) + lineMeasures (Lines) -> series
                    if (Array.isArray(config.xAxis) && config.xAxis.length > 0) {
                        xField = typeof config.xAxis[0] === 'string' ? config.xAxis[0] : (config.xAxis[0].name || config.xAxis[0].field);
                    }
                    // Combine Bars (yAxis) and Lines (lineMeasures)
                    const bars = config.yAxis || [];
                    const lines = (config.lineMeasures || []).map(s => ({ ...s, type: 'line' })); // Tag as line if needed
                    sourceSeries = [...bars, ...lines];

                } else {
                    // Default Cartesian (Bar, Line, Area, Pie)
                    if (Array.isArray(config.xAxis) && config.xAxis.length > 0) {
                        const xObj = config.xAxis[0];
                        xField = typeof xObj === 'string' ? xObj : (xObj?.name || xObj?.field);
                    } else {
                        // console.log("[useChartDataNew] xAxis invalid or empty");
                    }
                }

                const ySeries = sourceSeries.map((s, idx) => {
                    const fieldName = typeof s === 'string' ? s : (s.name || s.field);
                    // BOXPLOT: always force 'none' aggregation to get raw values for distribution
                    const agg = chartType === 'boxplot' ? 'none' : normalizeAgg(s.agg);
                    return {
                        id: s.id || `s_${idx}`,
                        field: fieldName,
                        name: s.name || fieldName,
                        agg: agg,
                        label: s.label || fieldName,
                        color: s.color || null,
                        role: s.role // Pass role (confidence, target, etc)
                    };
                }).filter(s => s.field);

                let columns = null;
                let pivotRows = null;
                let pivotCols = null;
                if (chartType === 'table') {
                    const rawCols = config.columns || config.query_config?.selected_columns;
                    // columns may be strings OR objects with name|field|id
                    columns = rawCols?.map(c =>
                        typeof c === 'string' ? c : (c?.name || c?.field || c?.id)
                    ).filter(Boolean) || [];
                } else if (chartType === 'pivot') {
                    // Send ALL rows, columns, and values for proper DuckDB pivot
                    pivotRows = (config.rows || []).map(r =>
                        typeof r === 'string' ? r : (r?.name || r?.field)
                    ).filter(Boolean);
                    pivotCols = (config.columns || []).map(c =>
                        typeof c === 'string' ? c : (c?.name || c?.field)
                    ).filter(Boolean);
                }

                // Pivot value fields with agg (sent separately so backend has field+agg without needing to
                // cross-reference y_series — avoids id mismatches)
                const pivotValues = chartType === 'pivot'
                    ? (config.values || []).map(v => ({
                        field: typeof v === 'string' ? v : (v?.name || v?.field),
                        agg: v?.agg || 'SUM',
                        id: v?.id || (typeof v === 'string' ? v : (v?.name || v?.field))
                    })).filter(pv => pv.field)
                    : null;

                const limit = config.query_config?.limit || config.data?.limit || 1000;
                // Relaxed Priority: attributes in 'data' take precedence, but fallback to 'query_config'
                // if not present. This ensures that if the UI only updates 'query_config' (legacy),
                // we still respect it even if 'data' object exists.
                let sortBy = config.data?.sort_by ?? config.query_config?.sortBy;

                let rawSortDir = config.query_config?.sortDir || config.query_config?.sortOrder || config.data?.sort_dir || 'desc';
                if (!rawSortDir) rawSortDir = 'desc';

                let sortDir = 'desc';
                const lowerSortDir = rawSortDir.toString().toLowerCase();

                if (lowerSortDir.includes('asc')) {
                    sortDir = 'asc';
                } else if (lowerSortDir.includes('desc')) {
                    sortDir = 'desc';
                }

                if (sortBy === 'Default' || !sortBy) {
                    sortBy = null;
                }

                if (sortBy) {
                    const matchedSeries = ySeries.find(s => s.field === sortBy || s.label === sortBy);
                    if (matchedSeries) {
                        sortBy = matchedSeries.id;
                    }
                }

                // Merge Widget Filters + Global Filters
                const globalFilterList = Object.entries(additionalFilters).map(([k, v]) => ({
                    field: k,
                    operator: Array.isArray(v) ? 'in' : 'equals',
                    value: v
                }));
                const mergedFilters = [...(config.filters || []), ...globalFilterList];

                const payload = {
                    dashboard_id: widget.dashboardId || widget.dashboard_id || null,
                    dataset_id: dataModel?.id ? undefined : (dataset?.id || undefined),
                    data_model_id: dataModel?.id || undefined,
                    limit: parseInt(limit),
                    x_field: xField,
                    y_series: ySeries,
                    columns: columns,
                    filters: mergedFilters,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                    group_field: groupField,
                    rank_by: null,
                    custom_measures: customMeasures,
                    source_date_format: config.query_config?.sourceDateFormat,
                    // Pivot-specific: full row/col/value field lists
                    ...(pivotRows ? { pivot_rows: pivotRows } : {}),
                    ...(pivotCols ? { pivot_cols: pivotCols } : {}),
                    ...(pivotValues ? { pivot_values: pivotValues } : {}),
                };

                // Pass Signal to API
                // Dashboard queries go to the interactive runner (port 8001)
                // so they run independently from the main API (port 8000)
                
                const isKpiWithComparison = ['kpi', 'gauge', 'ragKpi'].includes(chartType) && ((config.comparison || []).length > 0);
                
                let result = null;
                
                if (isKpiWithComparison && mergedFilters.length > 0) {
                    let hasShiftableFilter = false;
                    
                    const shiftDateBackwardSingle = (val) => {
                        if (typeof val !== 'string') return val;
                        const mmmMatch = val.match(/^([a-zA-Z]+)\s*(\d{4})$/);
                        if (mmmMatch) {
                           const monthStr = mmmMatch[1];
                           const year = parseInt(mmmMatch[2], 10);
                           const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
                           const fullMonths = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
                           
                           let lowerMonth = monthStr.toLowerCase();
                           let mIdx = fullMonths.indexOf(lowerMonth);
                           let isFull = true;
                           if (mIdx === -1) {
                               mIdx = months.indexOf(lowerMonth.substring(0, 3));
                               isFull = false;
                           }
                           
                           if (mIdx !== -1) {
                               mIdx -= 1;
                               let newYear = year;
                               if (mIdx < 0) {
                                   mIdx = 11;
                                   newYear -= 1;
                               }
                               let newMonthStr = isFull ? fullMonths[mIdx] : months[mIdx];
                               if (monthStr[0] === monthStr[0].toUpperCase()) newMonthStr = newMonthStr.charAt(0).toUpperCase() + newMonthStr.slice(1);
                               if (monthStr === monthStr.toUpperCase()) newMonthStr = newMonthStr.toUpperCase();
                               
                               const space = val.includes(' ') ? ' ' : '';
                               hasShiftableFilter = true;
                               return `${newMonthStr}${space}${newYear}`;
                           }
                        }
                        
                        const isoMatch = val.match(/^(\d{4})-(\d{2})$/);
                        if (isoMatch) {
                            let y = parseInt(isoMatch[1], 10);
                            let m = parseInt(isoMatch[2], 10);
                            m -= 1;
                            if (m < 1) { m = 12; y -= 1; }
                            hasShiftableFilter = true;
                            return `${y}-${m.toString().padStart(2, '0')}`;
                        }
                        
                        const slashMatch = val.match(/^(\d{1,2})\/(\d{4})$/);
                        if (slashMatch) {
                            let m = parseInt(slashMatch[1], 10);
                            let y = parseInt(slashMatch[2], 10);
                            m -= 1;
                            if (m < 1) { m = 12; y -= 1; }
                            hasShiftableFilter = true;
                            return `${m.toString().padStart(slashMatch[1].length, '0')}/${y}`;
                        }
                        return val;
                    };

                    const shiftDateBackward = (val) => {
                        if (Array.isArray(val)) {
                            return val.map(shiftDateBackwardSingle);
                        }
                        return shiftDateBackwardSingle(val);
                    };
                    
                    const shiftedFilters = mergedFilters.map(f => {
                         if (!f.value) return f;
                         const shiftedVal = shiftDateBackward(f.value);
                         return { ...f, value: shiftedVal };
                    });
                    
                    if (hasShiftableFilter) {
                         const [mainResult, prevResult] = await Promise.all([
                             interactiveFetch('/dashboards/query', null, {
                                 method: 'POST', body: JSON.stringify(payload), signal: controller.signal
                             }).catch(() => null),
                             interactiveFetch('/dashboards/query', null, {
                                 method: 'POST', body: JSON.stringify({ ...payload, filters: shiftedFilters }), signal: controller.signal
                             }).catch(() => null)
                         ]);
                         
                         result = mainResult;
                         let mainData = Array.isArray(mainResult?.data) ? mainResult.data : (Array.isArray(mainResult) ? mainResult : []);
                         let prevData = Array.isArray(prevResult?.data) ? prevResult.data : (Array.isArray(prevResult) ? prevResult : []);
                         
                         if (mainData.length > 0 && prevData.length > 0) {
                             const compIds = (config.comparison || []).map(c => c.id || c.name || c.field);
                             compIds.forEach(id => {
                                 if (prevData[0].hasOwnProperty(id)) {
                                     mainData[0][id + '_prev'] = prevData[0][id]; // Merge uniquely for comparison
                                 }
                             });
                         }
                    } else {
                        result = await interactiveFetch('/dashboards/query', null, {
                            method: 'POST', body: JSON.stringify(payload), signal: controller.signal
                        });
                    }
                } else {
                    result = await interactiveFetch('/dashboards/query', null, {
                        method: 'POST', body: JSON.stringify(payload), signal: controller.signal
                    });
                }

                let rawData = [];
                if (result && Array.isArray(result.data)) {
                    rawData = result.data;
                } else if (Array.isArray(result)) {
                    rawData = result;
                } else {
                    console.warn("Unexpected data format", result);
                    rawData = [];
                }

                if (rawData.length > 0) {
                    rawData = rawData.map(d => {
                        const newRow = { ...d };

                        if (xField && d.hasOwnProperty('x_val')) {
                            newRow[xField] = d.x_val;
                        }

                        ySeries.forEach(s => {
                            if (d.hasOwnProperty(s.id)) {
                                newRow[s.field] = d[s.id];
                                newRow[s.id] = d[s.id]; // Expose ID directly to avoid field collisions
                            }
                        });

                        return newRow;
                    });
                }

                // Only set data if not aborted (though catch handles it, double check)
                if (!controller.signal.aborted) {
                    setData(rawData);
                }

            } catch (err) {
                // Ignore Abort Errors
                if (err.name === 'AbortError') {
                    // console.log('[useChartDataNew] Request aborted');
                    return;
                }

                console.error("Failed to fetch chart data:", err);
                const errorMsg = err?.message || err?.error || err?.toString() || 'An error occurred while fetching data.';
                if (!controller.signal.aborted) {
                    setError({ message: errorMsg });
                    setData([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        // Cleanup: Abort request on unmount or re-execute
        return () => {
            controller.abort();
        };

    }, [dataKey]);

    return { data, loading, error };
};